import { useState, useEffect } from 'react'
import { MapPin, Heart, Info, AlertCircle, Filter } from 'lucide-react'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { catService } from './services/catService'
import CatMap from './components/CatMap'
import CatForm from './components/CatForm'
import Header from './components/Header'
import AuthModal from './components/Auth/AuthModal'
import SearchFilters from './components/SearchFilters'
import SetupNotification from './components/SetupNotification'
import MobileDebugInfo from './components/MobileDebugInfo'
import AdminDashboard from './components/Admin/AdminDashboard'
import OAuthCallback from './components/Auth/OAuthCallback'
import { isSupabaseConfigured, createDemoData, logDatabaseHealth, handleDatabaseError } from './utils/databaseHealth'
import './App.css'

function AppContent() {
  const { user, profile, loading: authLoading } = useAuth()
  const [cats, setCats] = useState([])
  const [filteredCats, setFilteredCats] = useState([])
  const [showForm, setShowForm] = useState(false)
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showAuthPrompt, setShowAuthPrompt] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [activeFilters, setActiveFilters] = useState({})
  const [currentView, setCurrentView] = useState('map') // 'map' or 'admin'

  // Check if this is an OAuth callback
  const isOAuthCallback = window.location.pathname === '/auth/callback' ||
                         window.location.hash.includes('access_token')

  // Load cats from Supabase on component mount
  useEffect(() => {
    loadCats()
    // Log database health for debugging
    logDatabaseHealth()
  }, [])

  // Set up real-time subscription
  useEffect(() => {
    const subscription = catService.subscribeToChanges((payload) => {
      console.log('Real-time update:', payload)

      if (payload.eventType === 'INSERT') {
        setCats(prev => [payload.new, ...prev])
        setFilteredCats(prev => [payload.new, ...prev])
      } else if (payload.eventType === 'UPDATE') {
        setCats(prev => prev.map(cat =>
          cat.id === payload.new.id ? payload.new : cat
        ))
        setFilteredCats(prev => prev.map(cat =>
          cat.id === payload.new.id ? payload.new : cat
        ))
      } else if (payload.eventType === 'DELETE') {
        setCats(prev => prev.filter(cat => cat.id !== payload.old.id))
        setFilteredCats(prev => prev.filter(cat => cat.id !== payload.old.id))
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  const loadCats = async () => {
    try {
      setLoading(true)
      setError(null)

      if (!isSupabaseConfigured()) {
        // Show demo data when Supabase is not configured
        console.log('Supabase not configured, showing demo data')
        const demoData = createDemoData()
        setCats(demoData)
        setFilteredCats(demoData)
        setLoading(false)
        return
      }

      const data = await catService.getAllCats()
      setCats(data)
      setFilteredCats(data)
    } catch (err) {
      console.error('Error loading cats:', err)

      // Use the database error handler for better error messages
      const errorInfo = handleDatabaseError(err, 'Loading cats')
      setError(errorInfo.userMessage)

      // If it's a schema issue, fall back to demo data
      if (errorInfo.technicalError.includes('does not exist')) {
        console.log('Database schema issue detected, falling back to demo data')
        const demoData = createDemoData()
        setCats(demoData)
        setFilteredCats(demoData)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleFiltersChange = async (filters) => {
    setActiveFilters(filters)

    // Check if any filters are active
    const hasActiveFilters = Object.values(filters).some(value =>
      value !== '' && value !== 10
    )

    if (!hasActiveFilters) {
      setFilteredCats(cats)
      return
    }

    try {
      // For location-based search, we need the user's current location
      // For now, we'll use a default location (Oklahoma City)
      const searchFilters = {
        ...filters,
        latitude: selectedLocation?.lat || 35.4676,
        longitude: selectedLocation?.lng || -97.5164
      }

      const searchResults = await catService.searchCats(searchFilters)
      setFilteredCats(searchResults)
    } catch (err) {
      console.error('Error searching cats:', err)
      setError('Failed to search cats. Please try again.')
    }
  }

  const handleMapClick = (location) => {
    setSelectedLocation(location)
    setShowForm(true)
  }

  const handleAddCat = async (catData) => {
    try {
      // Use location from catData if available (from geolocation), otherwise use selectedLocation
      const locationToUse = catData.location || selectedLocation

      const newCatData = {
        ...catData,
        user_id: user ? user.id : null,
        latitude: locationToUse.lat,
        longitude: locationToUse.lng,
        status: 'active'
      }

      // Only add reporter_type if user is authenticated (for backward compatibility)
      // Anonymous reports will work without this field
      if (user) {
        newCatData.reporter_type = 'authenticated'
      } else {
        // Try to add anonymous reporter type, but don't fail if the database doesn't support it
        newCatData.reporter_type = 'anonymous'
      }

      // Remove the location object from catData as it's not a database field
      delete newCatData.location

      const newCat = await catService.createCat(newCatData)
      // Real-time subscription will handle adding to the list
      setShowForm(false)
      setSelectedLocation(null)

      // Show success message with different content for anonymous users
      if (!user) {
        alert('Cat report submitted successfully! Thank you for helping the community. Sign up to manage your reports and get updates.')
      }
    } catch (err) {
      console.error('Error adding cat:', err)

      // Use the database error handler for consistent error messages
      const errorInfo = handleDatabaseError(err, 'Adding cat report')
      alert(errorInfo.userMessage)
    }
  }

  const handleCloseForm = () => {
    setShowForm(false)
    setSelectedLocation(null)
  }

  // Handle OAuth callback
  if (isOAuthCallback) {
    return <OAuthCallback />
  }

  if (authLoading || loading) {
    return (
      <div className="app">
        <Header />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="app">
      <Header
        currentView={currentView}
        onViewChange={setCurrentView}
        isAdmin={profile?.is_admin}
      />

      {currentView === 'admin' ? (
        <AdminDashboard />
      ) : (
        <main className="main-content">
          <div className="map-container">
            <CatMap
              cats={filteredCats}
              onMapClick={handleMapClick}
            />
          </div>

        <div className="info-panel">
          {error && (
            <div className="error-message">
              <AlertCircle className="error-icon" />
              <p>{error}</p>
              <button onClick={loadCats} className="retry-button">
                Retry
              </button>
            </div>
          )}

          <div className="panel-actions">
            <button
              className="filter-toggle-button"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="filter-toggle-icon" />
              Filters
            </button>
          </div>

          {showFilters && (
            <SearchFilters
              onFiltersChange={handleFiltersChange}
              onClose={() => setShowFilters(false)}
            />
          )}

          <div className="stats">
            <div className="stat-item">
              <MapPin className="stat-icon" />
              <span className="stat-number">{filteredCats.length}</span>
              <span className="stat-label">
                {filteredCats.length === cats.length ? 'Cats Reported' : 'Cats Found'}
              </span>
            </div>
          </div>

          <div className="instructions">
            <div className="instruction-item">
              <Info className="instruction-icon" />
              <p>
                Click anywhere on the map to report a homeless or lost cat
                {!user && " (anonymous reports welcome!)"}
              </p>
            </div>
            <div className="instruction-item">
              <Heart className="instruction-icon" />
              <p>Help build a community-driven database to assist with cat rescue efforts</p>
            </div>
          </div>
        </div>
        </main>
      )}

      {showForm && (
        <CatForm
          location={selectedLocation}
          onSubmit={handleAddCat}
          onClose={handleCloseForm}
        />
      )}

      <AuthModal
        isOpen={showAuthPrompt}
        onClose={() => setShowAuthPrompt(false)}
        initialMode="signup"
      />

      <MobileDebugInfo />
    </div>
  )
}

function App() {
  return (
    <AuthProvider>
      <SetupNotification />
      <AppContent />
    </AuthProvider>
  )
}

export default App
